/** Fluid drag-to-resize for the split between the assistant column and the
 *  surface column, clamped so neither pane can be squeezed unusably thin.
 *  Below MOBILE_BREAK the assistant hides behind a floating button instead
 *  (no meaningful split to resize).
 *
 *  CLONED from the Console (tc-lab src/useResizablePanel.ts) — see that file
 *  for the robustness notes (pointer capture, selection suppression,
 *  rAF-throttling, commit-on-drag-end, ARIA separator). LV deltas:
 *   - PANEL_BAR_HEIGHT is 48, not 64: the LV strip row is slimmer than the
 *     console's bars, and both LV column bars pin to this one height so their
 *     divider lines read as a single rule.
 *   - the resized pane is the RIGHT column (surfaces), same as the console. */
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react'

export const MOBILE_BREAK = 700
export const MIN_PANEL = 340
export const MIN_CHAT = 360
/** Hit-zone width of the handle. Exported so the chat pane reserves EXACTLY
 *  this much (the console once subtracted 6px for a 12px handle and the flex
 *  row overran the viewport by 6px). */
export const HANDLE_WIDTH = 16
/** The ONE bar height for both column headers — the assistant's bar and the
 *  surface strip. Both columns start at the same viewport top, so their
 *  header divider lines only read as one continuous rule when the heights
 *  are identical (border-box, border included). LV: 48px (console: 64). */
export const PANEL_BAR_HEIGHT = 48
/** Pointer travel (px) past which a gesture counts as a drag, not a click. */
const DRAG_SLOP = 3

function useViewportWidth(): number {
  const [w, setW] = useState(() => window.innerWidth)
  useEffect(() => {
    function onResize() { setW(window.innerWidth) }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return w
}

export function useResizablePanel(initial: number, onCommit: (w: number) => void) {
  const viewportWidth = useViewportWidth()
  const isMobile = viewportWidth < MOBILE_BREAK
  const [width, setWidth] = useState(initial)
  const [dragging, setDragging] = useState(false)
  const dragStart = useRef<{ x: number; w: number } | null>(null)
  const latest = useRef(initial)
  const moved = useRef(false)

  const clamp = useCallback((w: number) => {
    const maxPanel = Math.max(MIN_PANEL, viewportWidth - MIN_CHAT)
    return Math.min(Math.max(w, MIN_PANEL), maxPanel)
  }, [viewportWidth])

  const apply = useCallback((w: number) => { const c = clamp(w); latest.current = c; setWidth(c) }, [clamp])

  /** Set the split AND persist it — for the presets menu and the keyboard. */
  const setSplit = useCallback((w: number) => { apply(w); onCommit(clamp(w)) }, [apply, clamp, onCommit])

  useEffect(() => {
    if (!dragging) return
    let raf = 0
    function onMove(e: PointerEvent) {
      if (!dragStart.current) return
      const x = e.clientX
      if (Math.abs(x - dragStart.current.x) > DRAG_SLOP) moved.current = true
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        if (!dragStart.current) return
        // Panel is on the right — dragging the handle left grows it.
        apply(dragStart.current.w + (dragStart.current.x - x))
      })
    }
    function onUp() { setDragging(false); dragStart.current = null }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    window.addEventListener('lostpointercapture', onUp)
    const body = document.body
    const prevSelect = body.style.userSelect
    const prevCursor = body.style.cursor
    body.style.userSelect = 'none'
    body.style.cursor = 'col-resize'
    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      window.removeEventListener('lostpointercapture', onUp)
      body.style.userSelect = prevSelect
      body.style.cursor = prevCursor
    }
  }, [dragging, apply])

  // Commit to storage only once the drag ends, not on every frame.
  const wasDragging = useRef(false)
  useEffect(() => {
    if (wasDragging.current && !dragging) onCommit(latest.current)
    wasDragging.current = dragging
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging])

  const onHandlePointerDown = useCallback((e: ReactPointerEvent) => {
    if (e.button !== 0) return
    e.preventDefault()
    moved.current = false
    dragStart.current = { x: e.clientX, w: latest.current }
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* not fatal — the overlay still covers it */ }
    setDragging(true)
  }, [])

  /** Did the gesture that just ended actually move? Lets the handle ignore
   *  the synthetic click that follows a captured drag. */
  const wasDrag = useCallback(() => moved.current, [])

  const onHandleKeyDown = useCallback((e: ReactKeyboardEvent) => {
    const step = e.shiftKey ? 96 : 24
    let next: number | null = null
    if (e.key === 'ArrowLeft') next = latest.current + step
    else if (e.key === 'ArrowRight') next = latest.current - step
    else if (e.key === 'Home') next = viewportWidth - MIN_CHAT
    else if (e.key === 'End') next = MIN_PANEL
    if (next == null) return
    e.preventDefault()
    setSplit(next)
  }, [setSplit, viewportWidth])

  const presets = [
    { label: 'One third', width: clamp(Math.round(viewportWidth / 3)) },
    { label: 'Half and half', width: clamp(Math.round(viewportWidth / 2)) },
    { label: 'Two thirds', width: clamp(Math.round((viewportWidth * 2) / 3)) },
    { label: 'Widest', width: clamp(viewportWidth - MIN_CHAT) },
    { label: 'Reset to default', width: clamp(initial) },
  ]

  return {
    width: isMobile ? viewportWidth : clamp(width),
    isMobile, dragging,
    onHandlePointerDown, onHandleKeyDown, wasDrag,
    setSplit, presets,
    min: MIN_PANEL, max: Math.max(MIN_PANEL, viewportWidth - MIN_CHAT),
  }
}
